import { useState, useEffect, useCallback } from 'react';
import { getBanners, getCompanySettings } from '../services/api';

// Fallback slides mirror live CMS data so first paint matches production
const fallbackSlides = [
  {
    title: 'Custom Manufacturing Solutions',
    subtitle: 'Sesuai Kebutuhan Anda',
    description: 'Kami menerima pesanan custom manufacturing sesuai dengan spesifikasi dan kebutuhan khusus industri Anda. Konsultasi gratis dengan tim ahli kami.',
    image: 'https://manufacture-api.afsreman.com/storage/home-banners/custom-manufacturing-solutions/WhatsApp-Image-2025-11-26-at-09.33.30_9684ed80.jpg',
    link_url: '/contact',
    link_text: 'Hubungi Kami',
  },
  {
    title: 'Solusi Manufaktur Terpercaya',
    subtitle: 'Partner Industri Anda',
    description: 'PT CITRA HERITAGE INDONESIA menyediakan solusi lengkap untuk kebutuhan manufaktur dan fabrikasi industri Anda dengan standar kualitas internasional.',
    image: 'https://manufacture-api.afsreman.com/storage/home-banners/solusi-manufaktur-terpercaya/20250417_174416edit.png',
    link_url: '/about',
    link_text: 'Tentang Kami',
  },
  {
    title: 'Produk Berkualitas Tinggi',
    subtitle: 'Tersertifikasi Internasional',
    description: 'Semua produk kami telah melalui quality control ketat dan memenuhi standar kualitas internasional ISO 9001:2015.',
    image: 'https://manufacture-api.afsreman.com/storage/home-banners/produk-berkualitas-tinggi/WhatsApp-Image-2025-10-08-at-09.37.48_6e86f53d.jpg',
    link_url: '/products',
    link_text: 'Jelajahi Produk',
  },
  {
    title: 'Precision Machining',
    subtitle: 'Teknologi CNC Terkini',
    description: 'Pengerjaan presisi dengan mesin CNC terkini untuk hasil yang akurat dan berkualitas tinggi. Melayani berbagai kebutuhan komponen industri.',
    image: 'https://manufacture-api.afsreman.com/storage/home-banners/precision-machining/DJI_20250916150202_0459_D-copy.JPG',
    link_url: '/services',
    link_text: 'Lihat Layanan',
  },
  {
    title: 'Pengalaman 15+ Tahun',
    subtitle: 'Dipercaya Ratusan Perusahaan',
    description: 'Dengan pengalaman lebih dari 15 tahun di industri manufaktur, kami telah dipercaya oleh ratusan perusahaan lokal dan internasional.',
    image: 'https://manufacture-api.afsreman.com/storage/home-banners/precision-machining/DJI_20250916150202_0459_D-copy.JPG',
    link_url: '/about',
    link_text: 'Tentang Kami',
  },
];

const defaultAchievements = [
  { number: '15+', label: 'Years of Experience' },
  { number: '500+', label: 'Machines Delivered' },
  { number: '120+', label: 'Satisfied Clients' },
  { number: 'ISO', label: '9001:2015 Certified' },
];

const Hero = () => {
  const [cur, setCur] = useState(0);
  const [slides, setSlides] = useState(fallbackSlides);
  const [ready, setReady] = useState(true);
  const [achievements, setAchievements] = useState(defaultAchievements);

  useEffect(() => {
    getBanners().then(data => {
      if (data && data.length > 0) {
        const mapped = data.map((b, i) => ({
          title: b.title || fallbackSlides[i]?.title || 'PT CITRA HERITAGE INDONESIA',
          subtitle: b.subtitle || `SLIDE ${String(i+1).padStart(2,'0')} / ${String(data.length).padStart(2,'0')}`,
          description: b.description || fallbackSlides[i]?.description || '',
          image: b.image || fallbackSlides[i]?.image || '',
          link_url: b.link_url || '#contact',
          link_text: b.link_text || 'Learn More ↗',
        }));
        setSlides(mapped);
        setCur(0);
      }
      setReady(true);
    }).catch(() => setReady(true));

    getCompanySettings().then(data => {
      if (data && data.achievements) {
        try {
          const parsed = typeof data.achievements === 'string' ? JSON.parse(data.achievements) : data.achievements;
          if (Array.isArray(parsed) && parsed.length > 0) {
            setAchievements(parsed);
          }
        } catch (e) {
          // keep fallback
        }
      }
    });
  }, []);

  const total = slides.length;
  const next = useCallback(() => setCur(c => (c + 1) % total), [total]);
  const prev = () => setCur(c => (c - 1 + total) % total);
  const goTo = (n) => setCur(n);

  useEffect(() => {
    const timer = setInterval(next, 5500);
    return () => clearInterval(timer);
  }, [next]);

  // Parse title to add <mark> to first word
  const renderTitle = (title) => {
    if (!title) return null;
    const words = title.split(' ');
    // Find a keyword to highlight (wrap in mark)
    if (words.length >= 2) {
      return (
        <>
          {words[0]}<br/><mark>{words[1]}</mark><br/>{words.slice(2).join(' ')}
        </>
      );
    }
    return <mark>{title}</mark>;
  };

  return (
    <section id="hero" style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.3s ease' }}>
      <div className="hero-geo"></div>
      <div className="hero-grid-lines"></div>

      <div style={{position:'relative',overflow:'hidden',flex:1}}>
        <div className="hero-slides">
          {slides.map((slide, i) => (
            <div className={`slide ${i === cur ? 'slide-active' : ''}`} key={i}>
              <div
                className={`slide-bg ${i === cur ? 'slide-bg-active' : ''}`}
                style={slide.image ? {backgroundImage:`url(${slide.image})`} : {}}
              ></div>
              <div className={`slide-inner ${i === cur ? 'slide-inner-active' : ''}`}>
                <div className="slide-index"><span></span> {slide.subtitle}</div>
                <h1 className="slide-h">{renderTitle(slide.title)}</h1>
                <p className="slide-body">{slide.description}</p>
                <div className="slide-cta">
                  <a href={slide.link_url || '#products'} className="btn btn-y">{slide.link_text || 'Learn More ↗'}</a>
                  <a href="#contact" className="btn btn-ghost">Request Quote</a>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="hero-nav">
          <button className="h-arr" onClick={prev}>←</button>
          <button className="h-arr" onClick={next}>→</button>
        </div>
      </div>

      <div className="hero-bar">
        {achievements.map((item, i) => (
          <div className="hb-stat" key={i}>
            <div>
              <div className="hb-num">{item.number}</div>
              <div className="hb-label">{item.label}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Hero;